import React from 'react';
import { Badge } from 'react-bootstrap';
import { MODALITY_OPTIONS, DAY_OPTIONS } from './CreateApplicationSection1';
import {
    MEETING_HOUR_OPTIONS,
    MEETING_TYPE_OPTIONS,
    meetingLabel,
    meetingsFromSaved,
} from './meetings';
import { PROGRAMS } from './CreateApplicationSection2';
import { SUPPORT_USE_CASE_LABELS } from './CreateApplicationSection4';
import {
    LA_SUPPORT_USE_CASE_LABELS,
    LA_NOMINEE_STATUS_OPTIONS,
    LA_NOMINEE_DECLINE_OPTIONS,
    computeLARatio,
    getDefaultProgramResponses,
    needsLARecruitingMessage,
} from './CreateApplicationSection5';
import { badgeTextFor } from '@/shared/utils/badgeText';

// The same words the form offers ("30 minutes", "1.5 hours")
export const weeklyLengthLabel = (hours) =>
    MEETING_HOUR_OPTIONS.find((o) => o.value === Number(hours))?.label ??
    `${hours} ${Number(hours) === 1 ? 'hour' : 'hours'}`;

// Named students for the programs still selected, with only the fields the
// server stores: a client-side key such as a row id is never sent, since the
// schema has no row ids and refuses one it can't cast. What a save of
// progress sends, so a row filled in part way (a nominee's email and
// 900-number before the name) is kept.
export function getSavedNamedStudents(namedStudents, selectedPrograms) {
    const selectedProgramKeys = new Set(selectedPrograms.map((p) => p.key));
    return namedStudents
        .filter((s) => selectedProgramKeys.has(s.forProgram))
        .map(({ name, email, studentId, forProgram }) => ({
            name,
            email,
            studentId,
            forProgram,
        }));
}

// The saved rows without blank-named ones, so they aren't shown or
// submitted. Submit is only reached once every required name is filled in,
// so this drops only optional rows left empty.
export function getRelevantNamedStudents(namedStudents, selectedPrograms) {
    return getSavedNamedStudents(namedStudents, selectedPrograms).filter((s) =>
        s.name?.trim()
    );
}

// The wizards' ranked selectedPrograms, in the saved application's shape
export function toProgramPreferences(selectedPrograms) {
    return selectedPrograms.map((p) => ({ programType: p.key, rank: p.rank }));
}

// Checkboxes each step checks on its own, so ticking one late doesn't clear
// the Review & Submit confirmation
const ACKNOWLEDGMENT_FIELDS = new Set([
    'acknowledgesContract',
    'acknowledgesLARequirements',
    'acknowledgesLAExclusion',
]);
// Taken from the defaults, so a new question is covered once it's added
// there, and the list never changes length
const PROGRAM_ANSWER_FIELDS = Object.entries(
    getDefaultProgramResponses()
).flatMap(([program, fields]) =>
    Object.keys(fields)
        .filter((field) => !ACKNOWLEDGMENT_FIELDS.has(field))
        .map((field) => [program, field])
);

// The answers Review & Submit's confirmation covers, as effect deps: every
// answer the summary shows except the acknowledgment checkboxes
export function getConfirmedAnswers({
    courseId,
    isSubmittedOnBehalf,
    courseConfirmation,
    selectedPrograms,
    courseContext,
    namedStudents,
    programResponses,
}) {
    return [
        courseId,
        isSubmittedOnBehalf,
        courseConfirmation,
        selectedPrograms,
        courseContext,
        namedStudents,
        ...PROGRAM_ANSWER_FIELDS.map(
            ([program, field]) => programResponses[program]?.[field]
        ),
    ];
}

function isBlank(value) {
    if (value == null) return true;
    if (typeof value === 'string') return !value.trim();
    if (Array.isArray(value)) return value.length === 0;
    return false;
}

function programName(key) {
    return PROGRAMS.find((p) => p.key === key)?.full ?? key.toUpperCase();
}

function labelFor(options, value) {
    return options.find((opt) => opt.value === value)?.label ?? value;
}

// `missing` is what a never-saved answer shows. null shows "Not provided".
function yesNo(value, missing = 'No') {
    if (value === undefined) return missing;
    return value === true ? 'Yes' : 'No';
}

function NotProvided() {
    return <span className="text-muted fst-italic">Not provided</span>;
}

// One question and its answer. A blank answer says "Not provided" instead
// of being hidden.
function Answer({ label, value, children }) {
    return (
        <>
            <dt className="col-sm-4">{label}</dt>
            <dd className="col-sm-8 text-break">
                {isBlank(value) ? <NotProvided /> : (children ?? value)}
            </dd>
        </>
    );
}

// An "Other" choice shows what was typed for it
function withOther(label, otherText) {
    return (
        <>
            {label}: {isBlank(otherText) ? <NotProvided /> : otherText}
        </>
    );
}

// `grouped` sections hold ProgramGroups instead of answers directly
function SummarySection({ id, title, grouped = false, children }) {
    const headingId = `${id}-heading`;
    return (
        <section aria-labelledby={headingId} className="mb-4">
            <h3 id={headingId} className="h6 fw-semibold border-bottom pb-1">
                {title}
            </h3>
            {grouped ? children : <dl className="row mb-0">{children}</dl>}
        </section>
    );
}

function ProgramGroup({ title, children }) {
    return (
        <>
            <h4 className="small fw-semibold text-muted text-uppercase mt-2">
                {title}
            </h4>
            <dl className="row mb-0">{children}</dl>
        </>
    );
}

// Read-only view of every answer on a course support application, grouped
// by wizard section (#477). Shared by Review & Submit and View Details.
// `application` uses the saved application's shape. Pass
// showConfirmation to also show the Review & Submit answer. Pass
// namedStudentsFiltered for a reader the server sends only their own
// programs' named students: a program with none received then shows no
// named-student line, since "Not provided" would say nobody was named.
function ApplicationSummary({
    application,
    idPrefix,
    showConfirmation,
    namedStudentsFiltered = false,
}) {
    const {
        course,
        isSubmittedOnBehalf,
        acknowledgesGeneralTerms,
        confirmsSubmissionAccuracy,
    } = application;
    // `|| {}` rather than defaults, which don't cover null
    const courseConfirmation = application.courseConfirmation || {};
    const programPreferences = application.programPreferences || [];
    const courseContext = application.courseContext || {};
    const namedStudents = application.namedStudents || [];
    const programSpecificResponses = application.programSpecificResponses || {};
    const meetings = meetingsFromSaved(courseConfirmation);
    // Saved before #609 asked the meeting type: the single old schedule,
    // not a meetings list. A draft just hasn't answered yet (same as
    // nomineeNotAsked).
    const meetingTypeNotAsked =
        !courseConfirmation.meetings?.length && application.status !== 'draft';
    const ranked = [...programPreferences].sort((a, b) => a.rank - b.rank);
    const selected = new Set(ranked.map((p) => p.programType));
    const relevantStudents = getRelevantNamedStudents(
        namedStudents,
        ranked.map((p) => ({ key: p.programType }))
    );
    const taStudents = relevantStudents.filter((s) => s.forProgram === 'ta');
    // Every LA nominee, one per LA requested at most (#557). Unfiltered, so
    // a half-filled nominee shows what was typed
    const laNominees = namedStudents.filter((s) => s.forProgram === 'la');
    const ta = programSpecificResponses.ta || {};
    const la = programSpecificResponses.la || {};
    const si = programSpecificResponses.si || {};
    const supportUseCases = courseContext.supportUseCases || [];
    const laSupportUseCases = la.supportUseCases || [];
    const laRatio = computeLARatio(courseConfirmation, la.requestedLACount);
    // Submitting requires this answer, so a non-draft without it was saved
    // before #462 asked it. A draft just hasn't answered yet.
    const nomineeNotAsked =
        la.nomineeStatus === undefined && application.status !== 'draft';
    const nomineeStatusText = nomineeNotAsked
        ? 'Not asked'
        : la.nomineeStatus &&
          labelFor(LA_NOMINEE_STATUS_OPTIONS, la.nomineeStatus);

    return (
        <div>
            <SummarySection
                id={`${idPrefix}-summary-course`}
                title="Eligibility & Course"
            >
                <Answer
                    label="Course"
                    value={
                        course &&
                        `${course.subject} ${course.courseNumber} — ${course.title}`
                    }
                />
                <Answer
                    label="Course enrollment"
                    value={courseConfirmation.enrollment}
                />
                <Answer
                    label="CRN"
                    value={
                        courseConfirmation.crn || courseConfirmation.formerCrn
                    }
                >
                    {courseConfirmation.crn || (
                        <>
                            {courseConfirmation.formerCrn}{' '}
                            <span className="text-muted">(no longer held)</span>
                        </>
                    )}
                </Answer>
                <Answer
                    label="Section #"
                    value={courseConfirmation.sectionNumber}
                />
                {/* Requests filed before the form asked have no answer */}
                <Answer
                    label="Part of term"
                    value={courseConfirmation.partOfTermName || 'Not asked'}
                >
                    {courseConfirmation.partOfTermCode === 'other'
                        ? withOther('Other', courseConfirmation.partOfTermName)
                        : undefined}
                </Answer>
                <Answer
                    label="Instructor of record"
                    value={isSubmittedOnBehalf ? 'No' : 'Yes'}
                />
                {isSubmittedOnBehalf && (
                    <>
                        <Answer
                            label="Instructor name"
                            value={courseConfirmation.instructorOfRecordName}
                        />
                        <Answer
                            label="Instructor email"
                            value={courseConfirmation.instructorOfRecordEmail}
                        />
                    </>
                )}
                <Answer
                    label="Course modality"
                    value={courseConfirmation.modality}
                >
                    {courseConfirmation.modality === 'other'
                        ? withOther('Other', courseConfirmation.otherModality)
                        : labelFor(
                              MODALITY_OPTIONS,
                              courseConfirmation.modality
                          )}
                </Answer>
                {meetings.map((meeting, i) => {
                    const label = (text) => meetingLabel(meetings, i, text);
                    const typeNotAsked = meetingTypeNotAsked && !meeting.type;
                    return (
                        <React.Fragment key={i}>
                            <Answer
                                label={label('Meeting type')}
                                value={
                                    typeNotAsked ? 'Not asked' : meeting.type
                                }
                            >
                                {typeNotAsked
                                    ? 'Not asked'
                                    : meeting.type === 'other'
                                      ? withOther('Other', meeting.otherType)
                                      : labelFor(
                                            MEETING_TYPE_OPTIONS,
                                            meeting.type
                                        )}
                            </Answer>
                            <Answer label={label('Days')} value={meeting.days}>
                                {meeting.days === 'Other'
                                    ? withOther('Other', meeting.otherDays)
                                    : labelFor(DAY_OPTIONS, meeting.days)}
                            </Answer>
                            <Answer label={label('Time')} value={meeting.time}>
                                {meeting.time === 'Other'
                                    ? withOther('Other', meeting.otherTime)
                                    : meeting.time}
                            </Answer>
                            <Answer
                                label={label('Room')}
                                value={meeting.room}
                            />
                        </React.Fragment>
                    );
                })}
            </SummarySection>

            <SummarySection
                id={`${idPrefix}-summary-programs`}
                title="Select Programs"
            >
                <Answer label="Programs requested" value={ranked}>
                    {ranked.map((pref) => {
                        const program = PROGRAMS.find(
                            (p) => p.key === pref.programType
                        );
                        return (
                            <Badge
                                key={pref.programType}
                                bg={program?.bg || ''}
                                text={badgeTextFor(program?.bg || '')}
                                className={`me-1 ${program?.badgeClass || ''}`}
                            >
                                {pref.rank}.{' '}
                                {program?.label ||
                                    pref.programType.toUpperCase()}
                            </Badge>
                        );
                    })}
                </Answer>
            </SummarySection>

            {selected.has('la') && (
                <SummarySection
                    id={`${idPrefix}-summary-la-contract`}
                    title="Acknowledgments & Contract"
                >
                    <Answer
                        label="LA contract acknowledged"
                        value={yesNo(la.acknowledgesContract, null)}
                    />
                    <Answer
                        label="LA requirements acknowledged"
                        // Undefined on apps saved before #404 asked it
                        value={yesNo(
                            la.acknowledgesLARequirements,
                            'Not asked'
                        )}
                    />
                </SummarySection>
            )}

            <SummarySection
                id={`${idPrefix}-summary-rationale`}
                title="Course Context"
            >
                <Answer
                    label="Rationale for support"
                    value={courseContext.rationaleForSupport}
                />
                <Answer
                    label="How peer support will help"
                    value={supportUseCases}
                >
                    {supportUseCases
                        .map((v) => SUPPORT_USE_CASE_LABELS.get(v) ?? v)
                        .join(', ')}
                </Answer>
                {supportUseCases.includes('other') && (
                    <Answer
                        label="Other use case"
                        value={courseContext.otherSupportUseCase}
                    />
                )}
                <Answer
                    label="Peer supporter benefit"
                    value={courseContext.peerSupporterBenefit}
                />
                <Answer
                    label="Anything else we should know?"
                    value={courseContext.additionalNotes}
                />
            </SummarySection>

            {selected.size > 0 && (
                <SummarySection
                    id={`${idPrefix}-summary-questionnaire`}
                    title="Program Details"
                    grouped
                >
                    {selected.has('ta') && (
                        <ProgramGroup title={programName('ta')}>
                            {(!namedStudentsFiltered ||
                                taStudents.length > 0) && (
                                <Answer
                                    label="Named students"
                                    value={taStudents}
                                >
                                    <ul className="list-unstyled mb-0">
                                        {taStudents.map((s, i) => (
                                            <li key={`${s.forProgram}-${i}`}>
                                                {s.name}, {s.email}
                                                {s.studentId
                                                    ? `, ${s.studentId}`
                                                    : ''}
                                            </li>
                                        ))}
                                    </ul>
                                </Answer>
                            )}
                            <Answer
                                label="Department chair approval obtained"
                                value={yesNo(ta.chairApprovalObtained)}
                            />
                            <Answer
                                label="Expected TA responsibilities"
                                value={ta.expectedResponsibilities}
                            />
                            <Answer
                                label="Understands a course may not get both a TA and an LA"
                                value={yesNo(ta.acknowledgesLAExclusion)}
                            />
                        </ProgramGroup>
                    )}

                    {selected.has('la') && (
                        <ProgramGroup title={programName('la')}>
                            <Answer
                                label="Number of LAs"
                                value={la.requestedLACount}
                            >
                                {la.requestedLACount}
                                {laRatio != null &&
                                    ` (student-to-LA ratio ${laRatio}:1)`}
                            </Answer>
                            <Answer
                                label="Naming a student"
                                value={nomineeStatusText}
                            />
                            {/* An app saved before #462 can have a
                                nominee but no nomineeStatus */}
                            {(la.nomineeStatus === 'selected' ||
                                (la.nomineeStatus === undefined &&
                                    laNominees.length > 0)) &&
                                !(
                                    namedStudentsFiltered &&
                                    laNominees.length === 0
                                ) && (
                                    <>
                                        {/* Numbered only when there are several,
                                        so a single nominee reads as before.
                                        "selected" with none saved still
                                        shows one as Not provided */}
                                        {(laNominees.length > 0
                                            ? laNominees
                                            : [{}]
                                        ).map((nominee, i) => {
                                            const label =
                                                laNominees.length > 1
                                                    ? `Nominee ${i + 1}`
                                                    : 'Nominee';
                                            return (
                                                <React.Fragment key={i}>
                                                    <Answer
                                                        label={`${label} name`}
                                                        value={nominee.name}
                                                    />
                                                    <Answer
                                                        label={`${label} email`}
                                                        value={nominee.email}
                                                    />
                                                    <Answer
                                                        label={`${label} 900 number`}
                                                        value={
                                                            nominee.studentId
                                                        }
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                        <Answer
                                            // One answer for every nominee
                                            label={
                                                laNominees.length > 1
                                                    ? 'If a nominee declines'
                                                    : 'If your nominee declines'
                                            }
                                            value={
                                                la.nomineeDeclinePreference
                                                    ? labelFor(
                                                          LA_NOMINEE_DECLINE_OPTIONS,
                                                          la.nomineeDeclinePreference
                                                      )
                                                    : nomineeNotAsked
                                                      ? 'Not asked'
                                                      : null
                                            }
                                        />
                                    </>
                                )}
                            <Answer
                                label="How LA support will be used"
                                value={laSupportUseCases}
                            >
                                {laSupportUseCases
                                    .map(
                                        (v) =>
                                            LA_SUPPORT_USE_CASE_LABELS.get(v) ??
                                            v
                                    )
                                    .join(', ')}
                            </Answer>
                            {laSupportUseCases.includes('other') && (
                                <Answer
                                    label="Other LA use case"
                                    value={la.otherSupportUseCase}
                                />
                            )}
                            <Answer
                                label="How you will use your LA each week"
                                value={la.expectedLAResponsibilities}
                            />
                            <Answer
                                label="Weekly prep plan"
                                value={la.weeklyPrepPlan}
                            />
                            <Answer
                                label="Weekly prep session"
                                // 0 counts as unanswered, same as
                                // Section 5's check
                                value={la.weeklyMeetingHours || null}
                            >
                                {`${weeklyLengthLabel(la.weeklyMeetingHours)}/week`}
                            </Answer>
                            <Answer
                                label="How you will evaluate your LA"
                                value={la.assessmentPlan}
                            />
                            {/* Only when asked for: the form keeps a message
                                no longer asked for, and the server drops it
                                on submit. Applications saved before the
                                nominee question show any message they have */}
                            {(la.nomineeStatus
                                ? needsLARecruitingMessage(
                                      la,
                                      laNominees.length
                                  )
                                : !isBlank(la.recruitingMessage)) && (
                                <Answer
                                    label="Recruiting message"
                                    value={la.recruitingMessage}
                                />
                            )}
                        </ProgramGroup>
                    )}

                    {selected.has('si') && (
                        <ProgramGroup title={programName('si')}>
                            <Answer
                                label="Bottleneck course"
                                value={yesNo(si.isBottleneckCourse)}
                            />
                            <Answer
                                label="Expected sessions per week"
                                value={si.expectedSessionsPerWeek}
                            />
                            <Answer
                                label="Instructor willing to collaborate"
                                value={yesNo(si.instructorWillingToCollaborate)}
                            />
                        </ProgramGroup>
                    )}
                </SummarySection>
            )}

            <SummarySection
                id={`${idPrefix}-summary-general`}
                title="General Acknowledgments"
            >
                <Answer
                    label="Acknowledged"
                    value={yesNo(acknowledgesGeneralTerms)}
                />
            </SummarySection>

            {showConfirmation && (
                <SummarySection
                    id={`${idPrefix}-summary-review`}
                    title="Review & Submit"
                >
                    <Answer
                        label="Confirmed accurate"
                        value={yesNo(confirmsSubmissionAccuracy)}
                    />
                </SummarySection>
            )}
        </div>
    );
}

export default ApplicationSummary;
